/**
 * When to auto-start the onboarding tour.
 *
 * Everything else in this system is opt-in via `?fg_tour=`. This is the one
 * place a tour starts on its own, so the conditions are deliberately narrow and
 * all four must hold:
 *
 *   1. A real customer session — never the shared public demo, which has its
 *      own tour and its own lead gate.
 *   2. The CFO surface is actually up, so there is something to point at.
 *   3. It has never run for this user before. Once dismissed, it stays dismissed.
 *   4. The feature flag is on.
 *
 * Auto-starting is the kind of thing that is delightful once and infuriating
 * twice, so the "seen" marker is written the moment it starts, not when it
 * finishes — a tour someone escaped out of has still been offered.
 */

import { isDemoSession } from '../../data/demoSession'
import { isTourEnabled } from './tourSession'

export const FIRST_LOGIN_TOUR_SLUG = 'cfo-first-login'

const SEEN_KEY = 'fusegrid_first_login_tour_seen'

export function hasSeenFirstLoginTour(): boolean {
  if (typeof window === 'undefined') return true
  try {
    return window.localStorage.getItem(SEEN_KEY) === '1'
  } catch {
    // Storage unavailable: treat as seen. Re-offering a tour on every page load
    // is far worse than never offering it.
    return true
  }
}

export function markFirstLoginTourSeen(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(SEEN_KEY, '1')
  } catch {
    /* ignore */
  }
}

export type FirstLoginContext = {
  /** True once the CFO surface has a workbook to render. */
  cfoReady: boolean
}

export function shouldStartFirstLoginTour(ctx: FirstLoginContext): boolean {
  if (!isTourEnabled()) return false
  if (!ctx.cfoReady) return false
  // The public demo has its own narrative and its own lead gate; stacking the
  // onboarding tour on top would fire two overlays at one visitor.
  if (isDemoSession()) return false
  if (hasSeenFirstLoginTour()) return false
  return true
}
